/**
 * Weapon charms — E6 (`design/economy/economy-rebuild.md` §4.4), the first of the two
 * zero-wire-cost categories.
 *
 * ## One mesh, one id, equipped GLOBALLY
 *
 * A charm is "one mesh reused across every weapon, hung off a socket" (§4.4's own words) — the
 * charm itself never changes shape or texture; only its colour/material handle does. Because
 * the mesh is identical no matter which gun it hangs off, there is nothing for a *per-slot*
 * charm to express that a single global equip does not already say: a player who owns Chrome
 * Fang wears the same Chrome Fang whether they are holding the AR or the revolver. Storing three
 * slot-parallel charm ids (the way `weaponSkins` does, because a finish genuinely differs per
 * gun) would triple the state for zero visual difference and, if it is ever wired to the match
 * server, triple the join payload for zero benefit. So: **one equipped charm per account**,
 * mirroring how the character skin (`shared/src/cosmetics.ts`) is a single global id rather than
 * a per-slot one — same shape, same reasoning.
 *
 * ## The wire implication (stated, not built here)
 *
 * §4.4 lists charms as "equip state only, zero per-event" — this file only defines *what* a
 * charm is; nothing here touches the wire. If and when a charm becomes visible in a match, the
 * natural home is exactly where the character skin already rides: a single id in the signed join
 * ticket (`MatchTicketClaims`, alongside `skin`) and the WELCOME/PLAYER_INFO join payload,
 * **never a snapshot field and never a per-tick or per-event byte**. Sixteen SKUs plus "none"
 * fits in a `u8` (unlike `weaponSkins`, which needed a `u16` once the roster's finishes outgrew
 * a byte) — so the cost, the day that wiring lands, is exactly one byte once per join, zero
 * bytes on every tick after. That wiring is out of this file's scope (it lives in
 * `backend/src/game/match/**` and `realtime/src/rooms/**`, both owned elsewhere); this comment
 * is the contract for whoever adds it.
 *
 * ## Ids
 *
 * `0` is `NO_CHARM` — every account starts bare, the same "0 = nothing equipped" convention
 * `STOCK_WEAPON_SKIN` uses. Real charms are `1..16`, stable once shipped (nothing here is
 * reused the way the weapon-skin catalogue was rewritten — ids are persisted in `UserItem` rows
 * the moment the first one is bought).
 */

export const NO_CHARM = 0;

/** Ids are stable and must never be reused — a `charm:<id>` `UserItem` row persists them. */
export const CHARM = {
  // ── The Credits ladder (1-4): the "entry tier of charms" the design doc promises the free
  // player (§2). Plain, unglazed materials — nothing here needed a gem to make.
  dogTag: 1,
  paracordKnot: 2,
  bottleCap: 3,
  spentShell: 4,
  // ── The Gems ladder (5-16), ascending. Costume jewellery and superstition, cheapest first.
  luckyDie: 5,
  rabbitsFoot: 6,
  compassBead: 7,
  skullCharm: 8,
  enamelPin: 9,
  brassKnuckle: 10,
  jadeCoin: 11,
  silverWolf: 12,
  neonShard: 13,
  chromeFang: 14,
  moltenCore: 15,
  voidSigil: 16,
} as const;

export type CharmId = (typeof CHARM)[keyof typeof CHARM];

export interface CharmDefinition {
  id: number;
  name: string;
  description: string;
  /** Multiplied into the shared charm mesh's material — the renderer's one per-SKU input,
   * since every charm reuses the same geometry hung off the same socket. sRGB hex. */
  tint: number;
  /** Optional emissive lift for a charm that should read as lit rather than painted — same
   * convention as `WeaponSkinVariant.emissive` in `weapon-skins.ts`. */
  glow?: number;
  /**
   * File stem of this charm's shop picture — `/game/ui/charms/<art>.png`.
   *
   * A charm is a physical object, and the shop drew it as a flat coloured dot until 2026-09-16
   * ("just circles of different colours, not items"). `tint` is what the 3D pendant in a match
   * is painted with; this is what the thing actually LOOKS like, and the two are different
   * questions — a brushed steel dog tag and a pink rabbit's foot are not distinguishable as
   * two grey-pink circles.
   */
  art: string;
  priceCoins: number;
  currency: 'SOFT' | 'PREMIUM';
}

export const CHARMS: Readonly<Record<number, CharmDefinition>> = {
  [CHARM.dogTag]: {
    id: CHARM.dogTag,
    art: 'dog-tag',
    name: 'Dog Tag',
    description: 'A stamped tag on a chain, worn blank.',
    tint: 0x8a8f96,
    priceCoins: 400,
    currency: 'SOFT',
  },
  [CHARM.paracordKnot]: {
    id: CHARM.paracordKnot,
    art: 'paracord-knot',
    name: 'Paracord Knot',
    description: 'A cobra-weave knot, tied off with too much cord to be practical.',
    tint: 0x556b2f,
    priceCoins: 550,
    currency: 'SOFT',
  },
  [CHARM.bottleCap]: {
    id: CHARM.bottleCap,
    art: 'bottle-cap',
    name: 'Bottle Cap',
    description: 'Crimped, flattened, and kept anyway.',
    tint: 0xc0392b,
    priceCoins: 700,
    currency: 'SOFT',
  },
  [CHARM.spentShell]: {
    id: CHARM.spentShell,
    art: 'spent-shell',
    name: 'Spent Shell',
    description: 'The one that mattered, drilled and strung.',
    tint: 0xb08d57,
    priceCoins: 800,
    currency: 'SOFT',
  },
  [CHARM.luckyDie]: {
    id: CHARM.luckyDie,
    art: 'lucky-die',
    name: 'Lucky Die',
    description: 'Always comes up sixes. Never trust it.',
    tint: 0xf5f5f0,
    priceCoins: 200,
    currency: 'PREMIUM',
  },
  [CHARM.rabbitsFoot]: {
    id: CHARM.rabbitsFoot,
    art: 'rabbits-foot',
    name: "Rabbit's Foot",
    description: 'Dyed, not real, still works.',
    tint: 0xe8c1d6,
    priceCoins: 300,
    currency: 'PREMIUM',
  },
  [CHARM.compassBead]: {
    id: CHARM.compassBead,
    art: 'compass-bead',
    name: 'Compass Bead',
    description: 'A tiny working compass. Points home, mostly.',
    tint: 0x2f6f4e,
    priceCoins: 350,
    currency: 'PREMIUM',
  },
  [CHARM.skullCharm]: {
    id: CHARM.skullCharm,
    art: 'skull-charm',
    name: 'Skull Charm',
    description: 'Grinning, undersized, unbothered.',
    tint: 0xe9e6de,
    glow: 0x1a1a1a,
    priceCoins: 400,
    currency: 'PREMIUM',
  },
  [CHARM.enamelPin]: {
    id: CHARM.enamelPin,
    art: 'enamel-pin',
    name: 'Enamel Pin',
    description: 'Glossy, bright, unmistakably not standard issue.',
    tint: 0xd94f70,
    priceCoins: 450,
    currency: 'PREMIUM',
  },
  [CHARM.brassKnuckle]: {
    id: CHARM.brassKnuckle,
    art: 'brass-knuckle',
    name: 'Brass Knuckle',
    description: 'A miniature, purely decorative — mostly.',
    tint: 0xc9a227,
    priceCoins: 550,
    currency: 'PREMIUM',
  },
  [CHARM.jadeCoin]: {
    id: CHARM.jadeCoin,
    art: 'jade-coin',
    name: 'Jade Coin',
    description: 'Square hole, worn smooth on one face.',
    tint: 0x3f8f6f,
    priceCoins: 600,
    currency: 'PREMIUM',
  },
  [CHARM.silverWolf]: {
    id: CHARM.silverWolf,
    art: 'silver-wolf',
    name: 'Silver Wolf',
    description: 'Head thrown back, cast in one piece.',
    tint: 0xc7ccd1,
    glow: 0x2a3b4a,
    priceCoins: 700,
    currency: 'PREMIUM',
  },
  [CHARM.neonShard]: {
    id: CHARM.neonShard,
    art: 'neon-shard',
    name: 'Neon Shard',
    description: 'A splinter of something that should not glow this way.',
    tint: 0x21e6ff,
    glow: 0x0d3a42,
    priceCoins: 800,
    currency: 'PREMIUM',
  },
  [CHARM.chromeFang]: {
    id: CHARM.chromeFang,
    art: 'chrome-fang',
    name: 'Chrome Fang',
    description: 'Mirror-polished, catches every muzzle flash.',
    tint: 0xe6e9ec,
    glow: 0x3a3f45,
    priceCoins: 900,
    currency: 'PREMIUM',
  },
  [CHARM.moltenCore]: {
    id: CHARM.moltenCore,
    art: 'molten-core',
    name: 'Molten Core',
    description: 'A bead of glass with fire still trapped inside it.',
    tint: 0xff6a2b,
    glow: 0x4a1a05,
    priceCoins: 1000,
    currency: 'PREMIUM',
  },
  [CHARM.voidSigil]: {
    id: CHARM.voidSigil,
    art: 'void-sigil',
    name: 'Void Sigil',
    description: 'A shape that is hard to look straight at. Top of the rack.',
    tint: 0x2a1240,
    glow: 0x7b2ff7,
    priceCoins: 1200,
    currency: 'PREMIUM',
  },
} as const;

export const CHARM_LIST: readonly CharmDefinition[] = Object.values(CHARMS);
export const MAX_CHARM_ID = Math.max(...CHARM_LIST.map((charm) => charm.id));

export function isKnownCharm(id: unknown): id is CharmId {
  return typeof id === 'number' && Number.isInteger(id) && Object.prototype.hasOwnProperty.call(CHARMS, id);
}

/** Item id under which ownership is recorded (`backend/src/game/sellback` sells it back through
 * this prefix without knowing charms exist as a category). */
export function charmItemId(id: number): string {
  return `charm:${id}`;
}

/** Where a charm's shop picture lives. One function, so the catalogue, the shop and the
 * wardrobe cannot spell the path three ways — the same rule `emoteVoicePath` follows. */
export function charmArtPath(art: string): string {
  return `/game/ui/charms/${art}.png`;
}
